import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { getAppScrollTop, scrollAppTo, subscribeAppScroll } from "../../lib/layout/appScroll";

/**
 * Rolagem entre rotas.
 *
 *   PUSH    → topo (entrar numa tela nova começa do começo)
 *   POP     → restaura onde o usuário estava (é isto que faz o "voltar" voltar
 *             de verdade: até 28/07/2026 esta tela rolava pro topo em TODA
 *             mudança de pathname, inclusive no voltar — sair de um livro e
 *             cair no topo de uma Home longa lê como "o back foi pro lugar
 *             errado", mesmo com a rota certa)
 *   REPLACE → não mexe. Replace é correção de URL, não navegação: a Busca
 *             reescreve `?q=` a cada tecla com `replace`, e rolar ali jogaria
 *             o usuário pro topo no meio da digitação.
 *
 * ── Este módulo é o ÚNICO dono da rolagem de navegação (BUG-170) ────────────
 *
 * Na volta de um livro para uma estante que refaz fetch, TRÊS forças escreviam
 * o scroll: a restauração daqui (que clampava em ~0 com o documento ainda nos
 * skeletons e desistia após 1 frame), efeitos de mount das páginas zerando por
 * cima (hoje `useScrollResetOnChange`, que pula o mount), e o restaurador
 * NATIVO do browser — `history.scrollRestoration` default é `"auto"`, e é ele
 * que re-aplicava a posição num salto TARDIO quando o dado chegava, fora do
 * ciclo de render. No WKWebView esse salto aterrissa em tiles que o compositor
 * ainda não rasterizou: tela de papel com a scrollbar no meio até o próximo
 * toque — o vídeo do relato, quadro a quadro.
 *
 * Por isso: `scrollRestoration = "manual"` (uma restauração só, a nossa) e a
 * restauração do POP ganhou uma JANELA DE INSISTÊNCIA — re-aplica o alvo a
 * cada frame enquanto o documento não o alcança (dado assíncrono chegando),
 * com teto de tempo, e desiste ao primeiro sinal de interferência: qualquer
 * posição que não foi a que nós escrevemos significa gesto do usuário ou outro
 * código legítimo, e aí a página é deles.
 *
 * ⚠️ Quem rola NÃO é sempre o documento. A partir do corte de desktop o app vive
 * dentro do `AppFrame` e quem rola é um div interno; falar com `window` ali não
 * dá erro, apenas lê 0 e escreve em lugar nenhum — a restauração do "voltar"
 * morreria em silêncio. Por isso tudo aqui passa por `lib/layout/appScroll.ts`.
 * A assinatura é refeita a cada `key` porque o alvo pode mudar junto com a rota
 * (nem toda rota está dentro do frame).
 */

/**
 * Por quanto tempo o POP insiste em alcançar a posição salva enquanto a tela
 * cresce (fetch da estante pública em rede móvel fica na casa de 1-2s; o teto
 * cobre a cauda sem prender a página para sempre quando o conteúdo novo é
 * genuinamente mais curto que o alvo).
 */
const POP_RESTORE_WINDOW_MS = 4000;

export function ScrollToTop() {
  const { key, pathname, search } = useLocation();
  /**
   * Endereço da entrada anterior. Entrada nova com o MESMO endereço não é
   * página nova: é modal com histórico ou passo do onboarding (24/09,
   * `lib/navigation/overlayHistory.ts`). Sem isto, abrir qualquer folha era
   * um PUSH e jogava a página de baixo para o topo.
   */
  const lastPathRef = useRef<string | null>(null);
  const navigationType = useNavigationType();
  const positionsRef = useRef<Map<string, number>>(new Map());
  /**
   * A entrada que está NA TELA, trocada no commit (efeito de layout). O
   * gravador de rolagem de cada entrada só escreve enquanto ela é a da tela
   * (BUG-331): o Router navega em transição, e a limpeza do efeito passivo da
   * entrada anterior roda DEPOIS da pintura — no meio, o `scrollAppTo(0)` do
   * PUSH dispara `scroll` no mesmo scroller e o gravador velho, ainda
   * assinado, gravava 0 como a posição da tela de onde se saiu. Medido em
   * harness com CPU 6×: Início → aba → voltar caía no topo 9/10.
   */
  const activeKeyRef = useRef(key);

  // O restaurador nativo compete com o daqui — e ganha, porque age DEPOIS,
  // quando o dado assíncrono já cresceu o documento. Com restauração própria,
  // ele só pode atrapalhar. Efeito, e não módulo-load, para o jsdom dos testes
  // conseguir observar; idempotente, então re-montar não custa nada.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Anota a posição da entrada ATUAL enquanto o usuário rola. Guardar aqui, e
  // não no cleanup, é deliberado: no cleanup o DOM da tela seguinte já entrou
  // e o browser pode ter grampeado o scrollY numa página mais curta — o valor
  // que sobraria seria o errado.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (activeKeyRef.current !== key) return;
        positionsRef.current.set(key, getAppScrollTop());
      });
    };
    const unsubscribe = subscribeAppScroll(onScroll);
    return () => {
      unsubscribe();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [key]);

  useLayoutEffect(() => {
    activeKeyRef.current = key;
    const path = pathname + search;
    const samePage = lastPathRef.current === path;
    lastPathRef.current = path;
    if (navigationType === "REPLACE") return;
    if (samePage) return;

    const saved = navigationType === "POP" ? positionsRef.current.get(key) : undefined;
    const target = saved ?? 0;

    scrollAppTo(target);
    if (target === 0) return;
    if (getAppScrollTop() === target) return;

    // O documento ainda não tem altura para o alvo (chunk lazy, fetch em voo):
    // o browser clampou. Insistir a cada frame até o conteúdo chegar é o que
    // faz o "voltar" restaurar DE VERDADE em página assíncrona — e é o nosso
    // salto acontecer dentro do ciclo de render, não o salto tardio do
    // restaurador nativo que deixava o WKWebView com tiles em branco.
    //
    // `lastSeen` é a posição APÓS a nossa escrita (já clampada). Se no frame
    // seguinte a posição atual difere dela, outra força mexeu — gesto do
    // usuário ou scroll programático legítimo de página — e a janela desiste
    // na hora: insistência é para vencer o clamp, nunca uma pessoa.
    let lastSeen = getAppScrollTop();
    let frame = 0;
    const deadline = performance.now() + POP_RESTORE_WINDOW_MS;
    const tick = () => {
      frame = 0;
      if (getAppScrollTop() !== lastSeen) return;
      scrollAppTo(target);
      lastSeen = getAppScrollTop();
      if (lastSeen === target) return;
      if (performance.now() >= deadline) return;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [key, navigationType, pathname, search]);

  return null;
}
